import { Popover } from "@headlessui/react";
import { useEffect, useMemo, useRef, useState } from "react";
import { cx } from "shared/utils";
import {
  Button,
  ButtonSize,
  ButtonVariant,
  IconButton,
  IconButtonSize,
  IconButtonVariant,
  Show
} from "shared/components";
import { ApplicationNote, DeleteNoteMutation, EditNoteMutation } from "./types";

import { getMentionNotificationId } from "./helpers";
import { NoteDisplay } from "./components/NoteDisplay";
import { HugeiconsIcon } from "@hugeicons/react";
import {
  Mail02Icon,
  MoreHorizontalIcon,
  Pen01Icon
} from "@hugeicons-pro/core-stroke-rounded";
import { NoteForm } from "./components/NoteForm";
import { useSearchParams } from "react-router-dom";
import { Card } from "shared/components/Card";
import { useAuthContext } from "providers/Auth";
import { isUnread } from "./helpers";
import { DateTime } from "luxon";

const buttonClassName =
  "w-full flex px-4 py-2.5 !text-base !text-content-1000 !font-medium hover:bg-alpha-40";

interface ActivityNoteProps {
  note: ApplicationNote;
  applicationId: string;
  deleteNoteMeta: DeleteNoteMutation;
  editNoteMeta: EditNoteMutation;
  propertyName?: string;
}

export const ActivityNote = ({
  applicationId,
  deleteNoteMeta,
  editNoteMeta,
  note,
  propertyName
}: ActivityNoteProps) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const ref = useRef<HTMLDivElement>(null);
  const [editMode, setEditMode] = useState<boolean>(false);
  const { auth } = useAuthContext();

  const isFocused = useMemo(
    () => Boolean(searchParams.get("note_id") === String(note.id)),
    [searchParams]
  );

  const canEditNote = useMemo(() => {
    if (!auth) return false;

    // User can edit their own notes
    return auth.id === note.author?.id;
  }, [auth, note]);

  useEffect(() => {
    const isFocused = Boolean(searchParams.get("note_id") === String(note.id));
    if (!isFocused || !ref.current) return;
    // eslint-disable-next-line compat/compat
    requestAnimationFrame(() => {
      ref.current?.scrollIntoView({ behavior: "smooth", block: "center" });
    });

    setTimeout(() => {
      setSearchParams(
        (prev) => {
          // eslint-disable-next-line compat/compat
          const newParams = new URLSearchParams(prev);
          newParams.delete("note_id");
          return newParams;
        },
        { replace: true }
      );
    }, 1000);
  }, []);

  const mentionEmailedUsers = useMemo(() => {
    if (!note.rich_text?.notifications?.length) return null;

    return note.rich_text.notifications!.reduce<
      { id: string; name: string; email: string }[]
    >((acc, notification) => {
      const user = notification.user;
      if (Boolean(notification.metadata?.is_mailed))
        acc.push({ id: String(user.id), name: user.name, email: user.email });
      return acc;
    }, []);
  }, [note]);

  return (
    <div
      className="animate-appear w-full max-w-[550px]"
      ref={ref}
      data-activity-id={getMentionNotificationId(note, auth!.id)}
    >
      <Card
        className={cx(
          "px-6 py-4 w-full bg-white transition-all duration-300 ease-in-out ring-2 ring-transparent transform",
          isFocused ? "!ring-primary-200 scale-105" : "scale-100"
        )}
      >
        <div className="flex justify-between relative mb-2">
          <p className="text-base font-bold text-content-1000">
            {note.author?.name}
          </p>

          <Show if={canEditNote}>
            <Popover className="absolute -right-4 -top-2">
              <Popover.Button>
                <IconButton
                  variant={IconButtonVariant.BASE}
                  size={IconButtonSize.SM}
                  borderLess
                  className="bg-transparent hover:bg-alpha-40"
                  HugeIcon={MoreHorizontalIcon}
                  strokeWidth={3}
                />
              </Popover.Button>
              <Popover.Panel className="absolute right-0 z-10 mt-2 w-[140px] rounded-lg border border-alpha-100 bg-background-1000 shadow-xl">
                <Popover.Button className="w-full">
                  <Button
                    size={ButtonSize.SM}
                    variant={ButtonVariant.TEXT}
                    onClick={() => setEditMode(true)}
                    className={buttonClassName}
                  >
                    Edit note
                  </Button>
                </Popover.Button>
                <Popover.Button className="w-full">
                  <Button
                    size={ButtonSize.SM}
                    variant={ButtonVariant.TEXT}
                    onClick={() => deleteNoteMeta.mutate(Number(note.id))}
                    className={cx(
                      buttonClassName,
                      "border-t border-alpha-40 !text-danger-500"
                    )}
                  >
                    Delete note
                  </Button>
                </Popover.Button>
              </Popover.Panel>
            </Popover>
          </Show>
        </div>

        <Show if={typeof note.message === "string"}>
          {editMode ? (
            <NoteForm
              applicationId={applicationId}
              propertyName={propertyName}
              onSubmit={async (data) => {
                await editNoteMeta.mutateAsync({
                  noteId: Number(note.id),
                  data
                });
                setEditMode(false);
              }}
              onCancel={() => setEditMode(false)}
              initialValue={
                note.rich_text?.document_html ||
                note.rich_text?.document_text ||
                note.message
              }
              existingAttachments={
                note.attachments?.map((attachment) => attachment.id) || []
              }
              isLoading={editNoteMeta.isPending}
            />
          ) : (
            <div>
              <div className="flex gap-2">
                {auth?.id && isUnread(note, auth.id) ? (
                  <span className="size-2 bg-primary-500 rounded-full ring-2 ring-alpha-40 mt-1.5" />
                ) : null}
                <NoteDisplay
                  content={
                    note.rich_text?.document_html ||
                    note.rich_text?.document_text ||
                    note.message ||
                    ""
                  }
                  attachments={note.attachments}
                />
              </div>

              <Show if={note.updated_at || mentionEmailedUsers?.length}>
                <div className="mt-4 pt-4 border-t border-alpha-40 space-y-2">
                  <Show if={note.updated_at}>
                    <div className="flex gap-2 items-center">
                      <HugeiconsIcon
                        icon={Pen01Icon}
                        className="text-content-200"
                        size="20"
                      />
                      <p className="text-content-400 font-normal text-sm">
                        Edited at{" "}
                        {DateTime.fromISO(note.updated_at).toFormat(
                          "MMM dd, yyyy 'at' h:mm a"
                        )}
                      </p>
                    </div>
                  </Show>

                  <Show if={mentionEmailedUsers?.length}>
                    {mentionEmailedUsers?.map((item) => {
                      return (
                        <div key={item.id} className="flex gap-2">
                          <HugeiconsIcon
                            icon={Mail02Icon}
                            className="text-content-200"
                            size="20"
                          />
                          <p className="text-content-400 font-normal text-sm">
                            {`${item.name} (${item.email}) was emailed about the mention`}
                          </p>
                        </div>
                      );
                    })}
                  </Show>
                </div>
              </Show>
            </div>
          )}
        </Show>
      </Card>

      <p className="flex justify-end mt-2 text-sm text-content-300 mr-4">
        {DateTime.fromISO(note.created_at).toFormat("MMM dd, yyyy 'at' h:mm a")}
      </p>
    </div>
  );
};
